import { ArrowLeft, FlaskConical, Download, FileJson, Trash2, Loader2 } from "lucide-react";
import { Button } from "./ui/button";

import { LOGO_URL } from "../config/constants";

export function DeleteSubmissionButton({ isDirector, onDelete }) {
  if (!isDirector) return null;
  return (
    <Button variant="outline" size="sm" onClick={onDelete} className="border-red-300 text-status-red hover:bg-status-red-dim gap-1 text-xs h-8" data-testid="delete-submission-btn">
      <Trash2 className="w-3.5 h-3.5" /> Delete
    </Button>
  );
}

export function DeleteSubmissionModal({ show, onClose, reason, setReason, onConfirm, deleting }) {
  if (!show) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="delete-modal">
      <div className="bg-white rounded-md shadow-2xl p-6 max-w-md w-full mx-4 border border-red-200">
        <h3 className="text-lg font-semibold text-status-red mb-3">Delete Submission</h3>
        <p className="text-sm text-slate-600 mb-4">This action cannot be undone. Please provide a reason for deletion.</p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason for deletion..."
          rows={3}
          className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 mb-4 resize-none"
          data-testid="delete-reason-input"
        />
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={deleting}>Cancel</Button>
          <Button size="sm" onClick={onConfirm} disabled={deleting || !reason.trim()} className="bg-status-red hover:bg-status-red text-white" data-testid="confirm-delete-btn">
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function SubmissionLayout({ children, navigate, title, subtitle, matterReference, badge, badgeColor = "blue", badgeIcon: BadgeIcon, isTest, actions, isDirector, onDelete }) {
  return (
    <div className="min-h-screen bg-[#F5F5F0]" data-testid="submission-detail">
      <nav className="sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Bonsai Law" className="w-10 h-10" />
              <div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 text-xs rounded-md bg-${badgeColor}-100 text-${badgeColor}-700 border border-${badgeColor}-300 flex items-center gap-1`}>
                    {BadgeIcon && <BadgeIcon className="w-3 h-3" />}
                    {badge}
                  </span>
                  {isTest && (
                    <span className="px-2 py-0.5 text-xs rounded-md bg-status-amber-dim text-status-amber border border-amber-300 flex items-center gap-1">
                      <FlaskConical className="w-3 h-3" />
                      TEST
                    </span>
                  )}
                </div>
                <h1 className="text-lg font-bold text-teal truncate max-w-[200px] sm:max-w-none">{title}</h1>
                {matterReference && (
                  <div className="text-2xl font-mono font-bold text-teal mt-1" data-testid="matter-reference-badge">
                    {matterReference}
                  </div>
                )}
                {subtitle && <p className="text-xs text-txt-muted">{subtitle}</p>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {actions}
              <DeleteSubmissionButton isDirector={isDirector} onDelete={onDelete} />
              <Button variant="outline" size="sm" onClick={() => navigate("/")} className="border-navy-border text-teal hover:bg-teal/5 gap-1" data-testid="back-to-dashboard">
                <ArrowLeft className="w-3.5 h-3.5" /> Dashboard
              </Button>
            </div>
          </div>
        </div>
      </nav>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="space-y-4">
          {children}
        </div>
      </main>
    </div>
  );
}

export function DetailSection({ title, icon: Icon, children, className = "" }) {
  return (
    <div className={`glass-card overflow-hidden ${className}`} data-testid={`section-${title?.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="p-4 border-b border-navy-border/60 bg-gradient-to-r from-navy to-transparent">
        <h2 className="font-semibold text-teal flex items-center gap-2 text-sm">
          {Icon && <Icon className="w-4 h-4" />}
          {title}
        </h2>
      </div>
      <div className="p-4">
        {children}
      </div>
    </div>
  );
}

export function DetailField({ label, value, highlight = false }) {
  return (
    <div className="flex justify-between py-2.5 border-b border-navy-border/40 last:border-0 gap-4">
      <span className="text-txt-muted text-sm shrink-0">{label}</span>
      <span className={`text-sm font-medium text-right ${highlight ? 'text-gold' : 'text-txt'}`}>
        {value || "\u2014"}
      </span>
    </div>
  );
}

export function DetailGrid({ children, cols = 2 }) {
  return (
    <div className={`grid grid-cols-1 ${cols === 2 ? 'lg:grid-cols-2' : cols === 3 ? 'lg:grid-cols-3' : ''} gap-4`}>
      {children}
    </div>
  );
}

export function StatusBadge({ status }) {
  const styles = {
    pending_review: "bg-status-amber-dim text-status-amber border-amber-200",
    pending_docx_review: "bg-blue-50 text-blue-700 border-blue-200",
    approved: "bg-teal-dim text-teal border-teal",
    submitted: "bg-purple-50 text-purple-700 border-purple-200",
    rejected: "bg-status-red-dim text-status-red border-red-200",
    draft: "bg-slate-50 text-slate-600 border-slate-200",
    checked: "bg-teal-dim text-teal border-teal",
    authorised: "bg-teal-dim text-teal border-teal",
  };
  const labels = {
    checked: "Verified",
    authorised: "Authorised",
  };
  const s = styles[status] || "bg-slate-50 text-slate-600 border-slate-200";
  const label = labels[status] || (status || "unknown").replace(/_/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase());
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${s}`} data-testid="submission-status">
      {label}
    </span>
  );
}

export function DownloadActions({ handleDownloadPDF, handleExportJson, downloadingPdf }) {
  return (
    <>
      {handleDownloadPDF && (
        <Button onClick={handleDownloadPDF} disabled={downloadingPdf} variant="outline" size="sm" className="border-navy-border text-teal hover:bg-teal/5 gap-1 text-xs h-8" data-testid="download-pdf-btn">
          {downloadingPdf ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          PDF
        </Button>
      )}
      {handleExportJson && (
        <Button onClick={handleExportJson} variant="outline" size="sm" className="border-navy-border text-teal hover:bg-teal/5 gap-1 text-xs h-8" data-testid="export-json-btn">
          <FileJson className="w-3.5 h-3.5" />
          JSON
        </Button>
      )}
    </>
  );
}
